/**
 * DefinicionesPage — Operational definitions (compact single page)
 * Row 1: Split (left) + Bank Accounts (right)
 * Row 2: Operational Parameters (compact grid)
 *
 * Editing pattern: always-editable inline inputs when canEdit=true.
 * Shared sticky save bar appears when any section has unsaved changes.
 * Split connects to real backend; the rest uses mock data until endpoints exist.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { Modal } from '@/components/ui/Modal';
import { Save, History, Building2, HelpCircle } from 'lucide-react';
import { useSplit } from '@/features/policies/hooks';
import { ConfirmSaveModal } from '@/features/policies/components';
import { getConfig, updateConfig } from '@/services/credit-engine.service';
import { ROUTES, PERMISSIONS, PARTNER_ID } from '@/constants';
import { useAuth, useToast } from '@/hooks';
import type { SplitConfigItem } from '@/types/policy.types';

const BREADCRUMBS = [
  { label: 'Inicio', path: ROUTES.DASHBOARD },
  { label: 'Operaciones', path: ROUTES.OPS_DEFINITIONS },
  { label: 'Definiciones', path: ROUTES.OPS_DEFINITIONS },
];

// ─── Mock data (until backend endpoints exist) ──────────────────────────────

interface BankAccounts {
  fiado: { clabe: string; bank_name: string };
  partner: { clabe: string; bank_name: string };
}

const MOCK_BANK_ACCOUNTS: BankAccounts = {
  fiado: { clabe: '012180015000000001', bank_name: 'BBVA Mexico' },
  partner: { clabe: '014180025000000002', bank_name: 'Santander' },
};

interface OperationalParams {
  weeks_to_default: number;
  income_window_days: number;
  min_deposit_amount: number;
  opening_fee_pct: number;
  opening_fee_financed: boolean;
  default_annual_rate: number;
  frequency_ranges: Record<FreqKey, { min: number; max: number }>;
  min_deposits: Record<FreqKey, number>;
}

const MOCK_PARAMS: OperationalParams = {
  weeks_to_default: 8,
  income_window_days: 90,
  min_deposit_amount: 1.0,
  opening_fee_pct: 0.04,
  opening_fee_financed: true,
  default_annual_rate: 1.5,
  frequency_ranges: {
    DAILY: { min: 0, max: 4 },
    WEEKLY: { min: 5, max: 11 },
    BIWEEKLY: { min: 12, max: 24 },
    MONTHLY: { min: 25, max: 35 },
  },
  min_deposits: { DAILY: 4, WEEKLY: 4, BIWEEKLY: 3, MONTHLY: 2 },
};

type FreqKey = 'DAILY' | 'WEEKLY' | 'BIWEEKLY' | 'MONTHLY';
const FREQ_ORDER: FreqKey[] = ['DAILY', 'WEEKLY', 'BIWEEKLY', 'MONTHLY'];
const FREQ_LABELS: Record<FreqKey, string> = {
  DAILY: 'Diaria',
  WEEKLY: 'Semanal',
  BIWEEKLY: 'Quincenal',
  MONTHLY: 'Mensual',
};

// ─── Page ───────────────────────────────────────────────────────────────────

export function DefinicionesPage() {
  const { split, updateSplit, isSaving: isSavingSplit, isLoading } = useSplit();
  const { hasPermission } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const canEdit = hasPermission(PERMISSIONS.CREDIT_POLICIES_EDIT);

  // ── Bank Accounts from API ──────────────────────────────────────────
  const bankQuery = useQuery({
    queryKey: ['config', 'bank-accounts'],
    queryFn: () => getConfig('bank-accounts', PARTNER_ID),
  });
  const bankFromApi: BankAccounts | null = (() => {
    const raw = bankQuery.data?.data;
    if (!Array.isArray(raw) || raw.length === 0) return null;
    // Desde item 011 el backend valida shape strict ({ fiado, partner } con
    // clabe + bank_name no vacios). El merge defensivo previo era necesario
    // pre-validation porque el shape mismatch (webapp nested vs schema flat)
    // hacia que el GET devolviera vacio — ya resuelto en credit-data 1.5.3.
    return raw[0] as BankAccounts;
  })();

  // ── Operational Params from API ─────────────────────────────────────
  const paramsQuery = useQuery({
    queryKey: ['config', 'operational-params'],
    queryFn: () => getConfig('operational-params', PARTNER_ID),
  });
  const paramsFromApi: OperationalParams | null = (() => {
    const raw = paramsQuery.data?.data;
    if (!Array.isArray(raw) || raw.length === 0) return null;
    // Backend devuelve los 8 campos completos desde Fase 35 (validateConfigItems
    // exige shape completa en operational-params). El merge defensivo previo
    // quedo como codigo muerto — eliminado.
    return raw[0] as OperationalParams;
  })();

  // ── Mutations ───────────────────────────────────────────────────────
  const bankMutation = useMutation({
    mutationFn: (data: BankAccounts) =>
      updateConfig('bank-accounts', { partnerId: PARTNER_ID, items: [data] }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['config', 'bank-accounts'] });
      toast.success('Cuentas bancarias actualizadas');
    },
    onError: () => toast.error('Error al actualizar cuentas bancarias'),
  });

  const paramsMutation = useMutation({
    mutationFn: (data: OperationalParams) =>
      updateConfig('operational-params', { partnerId: PARTNER_ID, items: [data] }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['config', 'operational-params'] });
      toast.success('Parametros operativos actualizados');
    },
    onError: () => toast.error('Error al actualizar parametros operativos'),
  });

  // ── Draft state (one per section) ──────────────────────────────────────
  const [splitDraft, setSplitDraft] = useState<SplitConfigItem[] | null>(null);
  const [bankDraft, setBankDraft] = useState<BankAccounts | null>(null);
  const [paramsDraft, setParamsDraft] = useState<OperationalParams | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showSplitInfo, setShowSplitInfo] = useState(false);
  const [showBankInfo, setShowBankInfo] = useState(false);
  const [showParamsInfo, setShowParamsInfo] = useState(false);

  const isDirty = splitDraft !== null || bankDraft !== null || paramsDraft !== null;

  // ── Split helpers ──────────────────────────────────────────────────────
  const splitItem = split?.[0] ?? null;
  const currentSplit = splitDraft?.[0] ?? splitItem;

  function updateSplitField<K extends keyof SplitConfigItem>(field: K, value: SplitConfigItem[K]) {
    const base = splitDraft ?? (split ? structuredClone(split) : null);
    if (!base || !base[0]) return;
    const first = base[0];
    const updated: SplitConfigItem[] = [{ ...first, [field]: value }, ...base.slice(1)];
    setSplitDraft(updated);
  }

  // ── Bank helpers ───────────────────────────────────────────────────────
  const currentBank = bankDraft ?? bankFromApi ?? MOCK_BANK_ACCOUNTS;

  function updateBank(entity: 'fiado' | 'partner', field: 'clabe' | 'bank_name', value: string) {
    const base = bankDraft ?? structuredClone(bankFromApi ?? MOCK_BANK_ACCOUNTS);
    setBankDraft({ ...base, [entity]: { ...base[entity], [field]: value } });
  }

  // ── Params helpers ────────────────────────────────────────────────────
  const currentParams = paramsDraft ?? paramsFromApi ?? MOCK_PARAMS;

  function updateParam<K extends keyof OperationalParams>(field: K, value: OperationalParams[K]) {
    const base = paramsDraft ?? structuredClone(paramsFromApi ?? MOCK_PARAMS);
    setParamsDraft({ ...base, [field]: value });
  }

  function updateFreqRange(freq: FreqKey, bound: 'min' | 'max', value: number) {
    const base = paramsDraft ?? structuredClone(paramsFromApi ?? MOCK_PARAMS);
    setParamsDraft({
      ...base,
      frequency_ranges: { ...base.frequency_ranges, [freq]: { ...base.frequency_ranges[freq], [bound]: value } },
    });
  }

  function updateMinDeposit(freq: FreqKey, value: number) {
    const base = paramsDraft ?? structuredClone(paramsFromApi ?? MOCK_PARAMS);
    setParamsDraft({ ...base, min_deposits: { ...base.min_deposits, [freq]: value } });
  }

  // ── Save / Discard ─────────────────────────────────────────────────────
  function discardAll() {
    setSplitDraft(null);
    setBankDraft(null);
    setParamsDraft(null);
  }

  function handleConfirm() {
    if (splitDraft) {
      updateSplit(splitDraft);
    }
    if (bankDraft) {
      bankMutation.mutate(bankDraft);
    }
    if (paramsDraft) {
      paramsMutation.mutate(paramsDraft);
    }
    setShowConfirm(false);
    setSplitDraft(null);
    setBankDraft(null);
    setParamsDraft(null);
  }

  // ── Split visual ──────────────────────────────────────────────────────
  const totalPoints = currentSplit
    ? currentSplit.fiado_rate_points + currentSplit.partner_rate_points
    : 0;
  const fiadoPct = totalPoints > 0 && currentSplit ? (currentSplit.fiado_rate_points / totalPoints) * 100 : 50;
  const partnerPct = 100 - fiadoPct;

  // ── Input classes ─────────────────────────────────────────────────────
  const numInput = 'h-8 w-16 rounded-md border border-border bg-muted/50 px-2 text-sm text-right focus:outline-none focus:ring-1 focus:ring-primary';
  const textInput = 'h-8 rounded-md border border-border bg-muted/50 px-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary';
  const clabeInput = 'h-8 w-48 rounded-md border border-border bg-muted/50 px-2 font-mono text-sm tracking-wider focus:outline-none focus:ring-1 focus:ring-primary';

  return (
    <div className="space-y-4">
      <PageHeader
        title="Definiciones Operativas"
        subtitle="Variables de operacion del modulo de credito"
        breadcrumbs={BREADCRUMBS}
        helpKey="ops-definitions"
        actions={
          <Link to={`${ROUTES.CONFIG_HISTORY}?configType=split,bank-accounts,operational-params`}>
            <Button variant="outline" size="sm" leftIcon={<History size={14} />}>
              Ver historial
            </Button>
          </Link>
        }
      />

      {/* ── Row 1: Split + Bank Accounts ────────────────────────────────── */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Split */}
        {isLoading ? (
          <Skeleton variant="rectangular" height={160} />
        ) : currentSplit ? (
          <Card
            title="Distribucion Financiera"
            description="Split de intereses anuales entre entidades"
            titleExtra={
              <button
                type="button"
                onClick={() => setShowSplitInfo(true)}
                className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                title="Como funciona"
              >
                <HelpCircle size={15} />
              </button>
            }
          >
            {/* Visual bar */}
            <div className="mb-2 h-6 w-full overflow-hidden rounded-lg flex">
              <div
                className="flex items-center justify-center bg-primary text-[10px] font-bold transition-all"
                style={{ width: `${fiadoPct}%`, color: '#1e293b' }}
              >
                {fiadoPct > 18 && `${currentSplit.fiado_rate_points}%`}
              </div>
              <div
                className="flex items-center justify-center bg-blue-500 text-[10px] font-medium text-white transition-all"
                style={{ width: `${partnerPct}%` }}
              >
                {partnerPct > 12 && `${currentSplit.partner_rate_points}%`}
              </div>
            </div>

            {/* Legend */}
            <div className="mb-3 flex items-center gap-4 text-[10px] text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <span className="inline-block h-2 w-2 rounded-sm bg-primary" />
                FiadoSA
              </span>
              <span className="flex items-center gap-1.5">
                <span className="inline-block h-2 w-2 rounded-sm bg-blue-500" />
                UNE-Credito
              </span>
              <span className="ml-auto font-medium text-foreground">Total: {totalPoints}%</span>
            </div>

            {/* Editable rows */}
            <div className="space-y-2">
              <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
                <div className="flex items-center gap-2">
                  <span className="inline-block h-2.5 w-2.5 rounded-sm bg-primary" />
                  <span className="text-sm font-medium">FiadoSA</span>
                </div>
                {canEdit ? (
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      min={0}
                      className={numInput}
                      value={currentSplit.fiado_rate_points}
                      onChange={(e) => updateSplitField('fiado_rate_points', Number(e.target.value))}
                    />
                    <span className="text-xs text-muted-foreground">%</span>
                  </div>
                ) : (
                  <span className="text-sm font-semibold">{currentSplit.fiado_rate_points}%</span>
                )}
              </div>

              <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
                <div className="flex items-center gap-2">
                  <span className="inline-block h-2.5 w-2.5 rounded-sm bg-blue-500" />
                  <span className="text-sm font-medium">UNE-Credito</span>
                </div>
                {canEdit ? (
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      min={0}
                      className={numInput}
                      value={currentSplit.partner_rate_points}
                      onChange={(e) => updateSplitField('partner_rate_points', Number(e.target.value))}
                    />
                    <span className="text-xs text-muted-foreground">%</span>
                  </div>
                ) : (
                  <span className="text-sm font-semibold">{currentSplit.partner_rate_points}%</span>
                )}
              </div>

              <div className="flex items-center justify-between rounded-lg border border-border/50 bg-muted/20 px-3 py-2">
                <div>
                  <span className="text-sm font-medium">IVA sobre intereses</span>
                </div>
                {canEdit ? (
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      step="1"
                      min={0}
                      max={100}
                      className={numInput}
                      value={Math.round(currentSplit.iva_rate * 100)}
                      onChange={(e) => updateSplitField('iva_rate', Number(e.target.value) / 100)}
                    />
                    <span className="text-xs text-muted-foreground">%</span>
                  </div>
                ) : (
                  <span className="text-sm font-semibold">{(currentSplit.iva_rate * 100).toFixed(0)}%</span>
                )}
              </div>
            </div>
          </Card>
        ) : (
          <Card title="Distribucion Financiera">
            <p className="py-4 text-center text-sm text-muted-foreground">Sin configuracion de split disponible.</p>
          </Card>
        )}

        {/* Bank Accounts */}
        <Card
          title="Cuentas Bancarias"
          description="CLABEs concentradoras para conciliacion"
          titleExtra={
            <button
              type="button"
              onClick={() => setShowBankInfo(true)}
              className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              title="Que son las cuentas bancarias"
            >
              <HelpCircle size={15} />
            </button>
          }
        >
          <div className="space-y-3">
            {/* FiadoSA */}
            <div className="flex items-start gap-3 rounded-lg border border-border p-3">
              <Building2 size={16} className="mt-1.5 shrink-0 text-primary" />
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-medium text-muted-foreground mb-1">FiadoSA</p>
                {canEdit ? (
                  <div className="flex items-center gap-2">
                    <input
                      className={clabeInput}
                      value={currentBank.fiado.clabe}
                      onChange={(e) => updateBank('fiado', 'clabe', e.target.value.replace(/\D/g, '').slice(0, 18))}
                      placeholder="CLABE"
                    />
                    <input
                      className={textInput + ' w-32'}
                      value={currentBank.fiado.bank_name}
                      onChange={(e) => updateBank('fiado', 'bank_name', e.target.value)}
                      placeholder="Banco"
                    />
                  </div>
                ) : (
                  <div className="flex items-baseline gap-3">
                    <p className="font-mono text-sm font-semibold tracking-wider">{currentBank.fiado.clabe}</p>
                    <p className="text-xs text-muted-foreground">{currentBank.fiado.bank_name}</p>
                  </div>
                )}
              </div>
            </div>

            {/* UNE-Credito */}
            <div className="flex items-start gap-3 rounded-lg border border-border p-3">
              <Building2 size={16} className="mt-1.5 shrink-0 text-blue-500" />
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-medium text-muted-foreground mb-1">UNE-Credito</p>
                {canEdit ? (
                  <div className="flex items-center gap-2">
                    <input
                      className={clabeInput}
                      value={currentBank.partner.clabe}
                      onChange={(e) => updateBank('partner', 'clabe', e.target.value.replace(/\D/g, '').slice(0, 18))}
                      placeholder="CLABE"
                    />
                    <input
                      className={textInput + ' w-32'}
                      value={currentBank.partner.bank_name}
                      onChange={(e) => updateBank('partner', 'bank_name', e.target.value)}
                      placeholder="Banco"
                    />
                  </div>
                ) : (
                  <div className="flex items-baseline gap-3">
                    <p className="font-mono text-sm font-semibold tracking-wider">{currentBank.partner.clabe}</p>
                    <p className="text-xs text-muted-foreground">{currentBank.partner.bank_name}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* ── Row 2: Operational Parameters ───────────────────────────────── */}
      <Card
        title="Parametros Operativos"
        description="Variables de control de procesos automaticos (hoy hardcodeadas en lambdas)"
        titleExtra={
          <button
            type="button"
            onClick={() => setShowParamsInfo(true)}
            className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            title="Como funcionan los parametros"
          >
            <HelpCircle size={15} />
          </button>
        }
      >
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {/* Left: key params */}
          <div className="space-y-2">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Cobranza y Deteccion</h4>

            <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
              <div>
                <p className="text-sm font-medium">Semanas para default</p>
                <p className="text-[11px] text-muted-foreground">Mora consecutiva para baja contable</p>
              </div>
              {canEdit ? (
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    className={numInput}
                    value={currentParams.weeks_to_default}
                    onChange={(e) => updateParam('weeks_to_default', Number(e.target.value))}
                  />
                  <span className="text-xs text-muted-foreground">sem</span>
                </div>
              ) : (
                <span className="text-sm font-semibold">{currentParams.weeks_to_default} <span className="font-normal text-muted-foreground">sem</span></span>
              )}
            </div>

            <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
              <div>
                <p className="text-sm font-medium">Ventana de ingresos</p>
                <p className="text-[11px] text-muted-foreground">Dias hacia atras para detectar patron (P1)</p>
              </div>
              {canEdit ? (
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    className={numInput}
                    value={currentParams.income_window_days}
                    onChange={(e) => updateParam('income_window_days', Number(e.target.value))}
                  />
                  <span className="text-xs text-muted-foreground">dias</span>
                </div>
              ) : (
                <span className="text-sm font-semibold">{currentParams.income_window_days} <span className="font-normal text-muted-foreground">dias</span></span>
              )}
            </div>

            <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
              <div>
                <p className="text-sm font-medium">Deposito minimo</p>
                <p className="text-[11px] text-muted-foreground">Monto minimo para considerar ingreso</p>
              </div>
              {canEdit ? (
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    step="0.1"
                    className={numInput + ' w-20'}
                    value={currentParams.min_deposit_amount}
                    onChange={(e) => updateParam('min_deposit_amount', Number(e.target.value))}
                  />
                  <span className="text-xs text-muted-foreground">USD</span>
                </div>
              ) : (
                <span className="text-sm font-semibold">${currentParams.min_deposit_amount.toFixed(2)} <span className="font-normal text-muted-foreground">USD</span></span>
              )}
            </div>

            <h4 className="pt-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Comisiones y Mora</h4>

            <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
              <div>
                <p className="text-sm font-medium">Comision por apertura</p>
                <p className="text-[11px] text-muted-foreground">% sobre el monto del credito</p>
              </div>
              {canEdit ? (
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    step="0.1"
                    min={0}
                    max={100}
                    className={numInput}
                    value={Number((currentParams.opening_fee_pct * 100).toFixed(2))}
                    onChange={(e) => updateParam('opening_fee_pct', Number(e.target.value) / 100)}
                  />
                  <span className="text-xs text-muted-foreground">%</span>
                </div>
              ) : (
                <span className="text-sm font-semibold">{(currentParams.opening_fee_pct * 100).toFixed(2)}%</span>
              )}
            </div>

            <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
              <div>
                <p className="text-sm font-medium">Comision financiada</p>
                <p className="text-[11px] text-muted-foreground">Si se suma al monto financiado o se cobra al desembolso</p>
              </div>
              {canEdit ? (
                <label className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={currentParams.opening_fee_financed}
                    onChange={(e) => updateParam('opening_fee_financed', e.target.checked)}
                    className="h-4 w-4 rounded border-border"
                  />
                  <span className="text-xs text-muted-foreground">{currentParams.opening_fee_financed ? 'Si' : 'No'}</span>
                </label>
              ) : (
                <span className="text-sm font-semibold">{currentParams.opening_fee_financed ? 'Si' : 'No'}</span>
              )}
            </div>

            <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
              <div>
                <p className="text-sm font-medium">Tasa de mora anual</p>
                <p className="text-[11px] text-muted-foreground">Tasa anual aplicada sobre saldo vencido</p>
              </div>
              {canEdit ? (
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    step="0.1"
                    min={0}
                    className={numInput}
                    value={Number((currentParams.default_annual_rate * 100).toFixed(2))}
                    onChange={(e) => updateParam('default_annual_rate', Number(e.target.value) / 100)}
                  />
                  <span className="text-xs text-muted-foreground">%</span>
                </div>
              ) : (
                <span className="text-sm font-semibold">{(currentParams.default_annual_rate * 100).toFixed(2)}%</span>
              )}
            </div>
          </div>

          {/* Right: frequency classification table */}
          <div>
            <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Clasificacion de Frecuencia</h4>
            <div className="overflow-x-auto rounded-lg border border-border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/30 text-left">
                    <th className="px-3 py-1.5 text-xs font-medium text-muted-foreground">Tipo</th>
                    <th className="px-3 py-1.5 text-right text-xs font-medium text-muted-foreground">Rango (dias)</th>
                    <th className="px-3 py-1.5 text-right text-xs font-medium text-muted-foreground">Min dep.</th>
                  </tr>
                </thead>
                <tbody>
                  {FREQ_ORDER.map((freq) => (
                    <tr key={freq} className="border-b border-border/50 last:border-0">
                      <td className="px-3 py-1.5 text-sm">{FREQ_LABELS[freq]}</td>
                      <td className="px-3 py-1.5 text-right">
                        {canEdit ? (
                          <span className="inline-flex items-center gap-1">
                            <input
                              type="number"
                              className="w-12 rounded border border-border bg-muted/50 px-1 py-0.5 text-right text-xs focus:outline-none focus:ring-1 focus:ring-primary"
                              value={currentParams.frequency_ranges[freq].min}
                              onChange={(e) => updateFreqRange(freq, 'min', Number(e.target.value))}
                            />
                            <span className="text-muted-foreground">-</span>
                            <input
                              type="number"
                              className="w-12 rounded border border-border bg-muted/50 px-1 py-0.5 text-right text-xs focus:outline-none focus:ring-1 focus:ring-primary"
                              value={currentParams.frequency_ranges[freq].max}
                              onChange={(e) => updateFreqRange(freq, 'max', Number(e.target.value))}
                            />
                          </span>
                        ) : (
                          <span className="text-sm">{currentParams.frequency_ranges[freq].min}–{currentParams.frequency_ranges[freq].max}</span>
                        )}
                      </td>
                      <td className="px-3 py-1.5 text-right">
                        {canEdit ? (
                          <input
                            type="number"
                            className="w-12 rounded border border-border bg-muted/50 px-1 py-0.5 text-right text-xs focus:outline-none focus:ring-1 focus:ring-primary"
                            value={currentParams.min_deposits[freq]}
                            onChange={(e) => updateMinDeposit(freq, Number(e.target.value))}
                          />
                        ) : (
                          <span className="text-sm font-medium">{currentParams.min_deposits[freq]}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </Card>

      {/* ── Shared Sticky Save Bar ──────────────────────────────────────── */}
      {isDirty && (
        <div className="sticky bottom-4 flex items-center justify-end gap-2 rounded-lg border bg-card p-2.5 shadow-lg">
          <span className="mr-auto text-xs text-muted-foreground">Cambios sin guardar</span>
          <Button variant="outline" size="sm" onClick={discardAll}>
            Descartar
          </Button>
          <Button variant="primary" size="sm" leftIcon={<Save size={14} />} onClick={() => setShowConfirm(true)}>
            Guardar
          </Button>
        </div>
      )}

      {/* ── Split Info Modal ───────────────────────────────────────────── */}
      <Modal isOpen={showSplitInfo} onClose={() => setShowSplitInfo(false)} title="Distribucion Financiera" size="md">
        <div className="space-y-5">
          <p className="text-sm text-muted-foreground">
            Cada credito genera intereses que se reparten entre FiadoSA (operador) y UNE-Credito (originador). El IVA se aplica sobre el total de intereses cobrados.
          </p>

          {/* Visual example */}
          <div className="rounded-lg border border-border p-4">
            <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Ejemplo: Credito de $5,000 MXN a 8 semanas</p>
            <div className="mb-2 h-5 w-full overflow-hidden rounded-lg flex">
              <div className="flex items-center justify-center bg-primary text-[9px] font-bold" style={{ width: '8.3%', color: '#1e293b' }} />
              <div className="flex items-center justify-center bg-blue-500 text-[9px] font-medium text-white" style={{ width: '91.7%' }}>
                55%
              </div>
            </div>
            <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
              <span className="flex items-center gap-1"><span className="inline-block h-1.5 w-1.5 rounded-full bg-primary" /> FiadoSA 5%</span>
              <span className="flex items-center gap-1"><span className="inline-block h-1.5 w-1.5 rounded-full bg-blue-500" /> UNE-Credito 55%</span>
            </div>

            <div className="mt-3 grid grid-cols-3 gap-2 text-center">
              <div className="rounded-md bg-muted/30 p-2">
                <p className="text-[10px] text-muted-foreground">Intereses totales</p>
                <p className="text-sm font-bold text-foreground">$800</p>
              </div>
              <div className="rounded-md p-2" style={{ backgroundColor: 'rgba(var(--color-primary-rgb, 59,130,246), 0.1)' }}>
                <p className="text-[10px] text-muted-foreground">FiadoSA (8.3%)</p>
                <p className="text-sm font-bold text-primary">$67</p>
              </div>
              <div className="rounded-md bg-blue-500/10 p-2">
                <p className="text-[10px] text-muted-foreground">UNE-Credito (91.7%)</p>
                <p className="text-sm font-bold text-blue-400">$733</p>
              </div>
            </div>
          </div>

          {/* Components explanation */}
          <div className="space-y-0">
            {[
              { icon: '1', title: 'FiadoSA %', text: 'Porcentaje anual de interes que retiene FiadoSA por operar la plataforma, administrar cobranza y mantener la infraestructura.' },
              { icon: '2', title: 'UNE-Credito %', text: 'Porcentaje anual de interes que recibe UNE-Credito como originador del credito y proveedor del capital.' },
              { icon: '3', title: 'IVA', text: 'Impuesto al Valor Agregado aplicado sobre el total de intereses cobrados al cliente. Se calcula sobre el interes, no sobre el capital.' },
            ].map((step, i) => (
              <div key={i} className="flex gap-3">
                <div className="flex flex-col items-center">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-sky-500/20 text-xs font-bold text-sky-400">
                    {step.icon}
                  </span>
                  {i < 2 && <div className="w-px flex-1 bg-border my-0.5" />}
                </div>
                <div className="pb-3">
                  <p className="text-sm font-semibold text-foreground">{step.title}</p>
                  <p className="text-xs text-muted-foreground">{step.text}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Important notes */}
          <div className="space-y-2">
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2">
              <p className="text-xs text-amber-400">
                <strong>Retroactividad:</strong> Los cambios al split aplican solo a creditos nuevos. Los creditos existentes conservan el split con el que fueron originados.
              </p>
            </div>
            <div className="rounded-lg bg-muted/50 px-3 py-2">
              <p className="text-xs text-muted-foreground">
                <strong className="text-foreground">Formula:</strong> Interes FiadoSA = interes_total × (fiado% / total%). Interes UNE-Credito = interes_total × (partner% / total%). IVA = interes_total × tasa_iva.
              </p>
            </div>
          </div>
        </div>
      </Modal>

      {/* ── Bank Info Modal ──────────────────────────────────────────── */}
      <Modal isOpen={showBankInfo} onClose={() => setShowBankInfo(false)} title="Cuentas Bancarias" size="md">
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            CLABEs de las cuentas concentradoras donde se depositan y dispersan los fondos del modulo de credito.
          </p>

          {/* Visual flow */}
          <div className="flex items-center justify-center gap-2 rounded-lg border border-border p-4">
            <div className="rounded-md bg-primary/10 px-3 py-2 text-center">
              <p className="text-[10px] text-muted-foreground">Cliente paga</p>
              <p className="text-xs font-bold text-primary">$$$</p>
            </div>
            <span className="text-muted-foreground">→</span>
            <div className="rounded-md bg-primary/10 px-3 py-2 text-center">
              <p className="text-[10px] text-muted-foreground">Concentradora</p>
              <p className="text-xs font-bold text-primary">FiadoSA</p>
            </div>
            <span className="text-muted-foreground">→</span>
            <div className="rounded-md bg-blue-500/10 px-3 py-2 text-center">
              <p className="text-[10px] text-muted-foreground">Dispersa a</p>
              <p className="text-xs font-bold text-blue-400">UNE-Credito</p>
            </div>
          </div>

          <div className="space-y-0">
            {[
              { icon: '1', title: 'CLABE FiadoSA', text: 'Cuenta concentradora donde se reciben todos los pagos de los clientes. Los fondos se concilian aqui antes de distribuirse.' },
              { icon: '2', title: 'CLABE UNE-Credito', text: 'Cuenta del originador a donde se transfiere la porcion correspondiente segun el split configurado.' },
            ].map((step, i) => (
              <div key={i} className="flex gap-3">
                <div className="flex flex-col items-center">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-sky-500/20 text-xs font-bold text-sky-400">
                    {step.icon}
                  </span>
                  {i < 1 && <div className="w-px flex-1 bg-border my-0.5" />}
                </div>
                <div className="pb-3">
                  <p className="text-sm font-semibold text-foreground">{step.title}</p>
                  <p className="text-xs text-muted-foreground">{step.text}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2">
            <p className="text-xs text-amber-400">
              <strong>Validacion:</strong> Las CLABEs deben tener exactamente 18 digitos numericos. Un cambio aqui no afecta transacciones ya conciliadas.
            </p>
          </div>
        </div>
      </Modal>

      {/* ── Params Info Modal ────────────────────────────────────────── */}
      <Modal isOpen={showParamsInfo} onClose={() => setShowParamsInfo(false)} title="Parametros Operativos" size="md">
        <div className="space-y-5">
          <p className="text-sm text-muted-foreground">
            Variables que controlan los procesos automaticos del motor de credito. Hoy estan hardcodeadas en el codigo de los lambdas.
          </p>

          {/* Cobranza y Deteccion */}
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Cobranza y Deteccion de Ingresos</p>
            <div className="space-y-0">
              {[
                { icon: '1', title: 'Semanas para default', text: 'Numero de semanas consecutivas en mora para que un credito pase a baja contable (DEFAULT). El proceso WeeklyClose evalua esto cada viernes.' },
                { icon: '2', title: 'Ventana de ingresos', text: 'Cuantos dias hacia atras analiza el motor P1 para detectar el patron de ingresos del cliente al evaluar su solicitud de credito.' },
                { icon: '3', title: 'Deposito minimo', text: 'Monto minimo en USD para que una transaccion se considere como ingreso real y no como un movimiento menor (ej: redondeo, cashback).' },
              ].map((step, i) => (
                <div key={i} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-sky-500/20 text-xs font-bold text-sky-400">
                      {step.icon}
                    </span>
                    {i < 2 && <div className="w-px flex-1 bg-border my-0.5" />}
                  </div>
                  <div className="pb-3">
                    <p className="text-sm font-semibold text-foreground">{step.title}</p>
                    <p className="text-xs text-muted-foreground">{step.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Clasificacion de Frecuencia */}
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Clasificacion de Frecuencia</p>
            <p className="mb-3 text-xs text-muted-foreground">
              El motor calcula el promedio de dias entre depositos del cliente y lo clasifica en una de estas categorias:
            </p>
            <div className="overflow-hidden rounded-lg border border-border">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="px-3 py-1.5 text-left font-medium text-muted-foreground">Frecuencia</th>
                    <th className="px-3 py-1.5 text-right font-medium text-muted-foreground">Rango (dias)</th>
                    <th className="px-3 py-1.5 text-left font-medium text-muted-foreground">Significa</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { freq: 'Diaria', range: '0–4', desc: 'Depositos casi todos los dias' },
                    { freq: 'Semanal', range: '5–11', desc: 'Un deposito por semana aprox.' },
                    { freq: 'Quincenal', range: '12–24', desc: 'Un deposito cada dos semanas' },
                    { freq: 'Mensual', range: '25–35', desc: 'Un deposito al mes' },
                  ].map((row, i) => (
                    <tr key={i} className="border-b border-border/50 last:border-0">
                      <td className="px-3 py-1.5 font-medium">{row.freq}</td>
                      <td className="px-3 py-1.5 text-right">{row.range}</td>
                      <td className="px-3 py-1.5 text-muted-foreground">{row.desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              <strong className="text-foreground">Min. depositos:</strong> Cantidad minima de depositos que debe tener el cliente en la ventana para que la frecuencia sea valida. Si no cumple el minimo, se descarta esa clasificacion.
            </p>
          </div>

          <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2">
            <p className="text-xs text-amber-400">
              <strong>Importante:</strong> Estos valores estan hardcodeados en los lambdas. Los cambios aqui se guardaran en la base de datos, pero solo tendran efecto cuando el backend lea de la DB en lugar del codigo.
            </p>
          </div>
        </div>
      </Modal>

      <ConfirmSaveModal
        isOpen={showConfirm}
        onConfirm={handleConfirm}
        onCancel={() => setShowConfirm(false)}
        isSaving={isSavingSplit}
      />
    </div>
  );
}
